import React, { useState, useRef, useEffect } from "react";
import { API } from "@/lib/constants";
import { toast } from "sonner";

// M0 — Proposal Studio (quality & editing proof). Isolated from the live
// proposal workflow. Drives /api/m0/proposal/* : create → render → revise.
const M0 = "/m0/proposal";

const SUGGESTIONS = [
  "Make the proposal cover title bold",
  "Change the leadership development programme from four sessions to six sessions",
  "Increase the leadership workshop fee to 3,200,000 per session and update every affected total",
  "Replace the cover image with https://customer-assets.emergentagent.com/job_resiligent-brief/artifacts/f5xtcilz_Proposal%20Generic%20background%201.png",
];

export default function ProposalStudioM0() {
  const [artifact, setArtifact] = useState(null);
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState("");
  const [instruction, setInstruction] = useState("");
  const [log, setLog] = useState([]);
  const [inspection, setInspection] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverQuery, setCoverQuery] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [orientation, setOrientation] = useState("portrait");
  const [showStock, setShowStock] = useState(false);
  const [stockCands, setStockCands] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [library, setLibrary] = useState({ uploads: [], onedrive_count: 0, onedrive_sample: [] });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadLibrary = async () => {
    try {
      const r = await fetch(`${API}${M0}/reference/library`, { credentials: "include" });
      if (r.ok) setLibrary(await r.json());
    } catch { /* ignore */ }
  };
  useEffect(() => { loadLibrary(); }, []);

  const uploadRef = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await fetch(`${API}${M0}/reference/upload`, { method: "POST", credentials: "include", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "upload failed");
      toast.success(`Added "${d.file_name}" (${d.chars.toLocaleString()} chars)`);
      pushLog("rex", `I've read "${d.file_name}" — I'll use it when drafting.`);
      await loadLibrary();
    } catch (err) { toast.error(err.message); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const logRef = useRef(null);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [log]);

  const pushLog = (role, text) => setLog((l) => [...l, { role, text, t: Date.now() }]);

  const loadPages = (art) => {
    const r = art?.render;
    if (!r) { setPages([]); return; }
    setPages((r.page_urls || []).map((u) => `${API}${u}?v=${art.version}`));
  };

  const create = async () => {
    setBusy("Rex is drafting the proposal for Union Bank…");
    pushLog("user", "Prepare a leadership development proposal for Union Bank based on our recent discussions.");
    try {
      const r = await fetch(`${API}${M0}/create`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: "{}" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "create failed");
      setBusy("Rendering the pages…");
      const rr = await fetch(`${API}${M0}/${d.artifact.artifact_id}/render`, { method: "POST", credentials: "include" });
      const rd = await rr.json();
      const art = { ...d.artifact, render: rd.render };
      setArtifact(art); loadPages(art); setInspection(null);
      const src = (art.sources_used || []);
      pushLog("rex", `Draft ready — v${art.version}. ${art.sections?.length || 0} sections, total ${art.commercials?.currency}${(art.commercials?.total || 0).toLocaleString()}.` +
        (src.length ? ` Grounded in ${src.length} reference doc(s): ${src.map((s) => s.file_name).join(", ")}.` : " (No reference library connected — used the built-in sample.)"));
      toast.success("Proposal drafted");
    } catch (e) { toast.error(e.message); pushLog("rex", `I hit a problem: ${e.message}`); }
    setBusy("");
  };

  const revise = async (text) => {
    const ins = (text ?? instruction).trim();
    if (!ins || !artifact) return;
    setBusy("Applying your change and re-rendering…");
    pushLog("user", ins);
    setInstruction("");
    try {
      const r = await fetch(`${API}${M0}/${artifact.artifact_id}/revise`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instruction: ins, expected_version: artifact.version }),
      });
      const d = await r.json();
      if (r.status === 409) { pushLog("rex", `Someone else changed this proposal (${d.detail}). Reloading latest.`); toast.error("Version conflict — reloaded"); await reload(); setBusy(""); return; }
      if (!r.ok) throw new Error(d.detail || "revise failed");
      if (!d.applied) { pushLog("rex", d.note || "I couldn't plan that change."); setBusy(""); return; }
      setArtifact(d.artifact); loadPages(d.artifact); setInspection(null);
      pushLog("rex", `Done (v${d.artifact.version}). ${d.change_log?.join("; ")}. New total ${d.artifact.commercials?.currency}${(d.artifact.commercials?.total || 0).toLocaleString()}.`);
      toast.success(`Applied — v${d.artifact.version}`);
    } catch (e) { toast.error(e.message); pushLog("rex", `That didn't work: ${e.message}`); }
    setBusy("");
  };

  const reload = async () => {
    const r = await fetch(`${API}${M0}/${artifact.artifact_id}`, { credentials: "include" });
    const d = await r.json(); setArtifact(d.artifact); loadPages(d.artifact);
  };

  const inspect = async () => {
    if (!artifact) return;
    setBusy("Rex is inspecting the rendered pages…");
    try {
      const r = await fetch(`${API}${M0}/${artifact.artifact_id}/inspect`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "inspect failed");
      setInspection(d.inspection);
      pushLog("rex", `Visual check: ${d.inspection.verdict}. ${(d.inspection.issues || []).length} issue(s).`);
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const setCover = async (method, extra = {}) => {
    if (!artifact) return;
    setCoverBusy(true); setBusy("Rex is updating the cover…");
    pushLog("user", `Cover · ${method}${extra.query ? ` "${extra.query}"` : ""}${extra.image_url ? " (my image)" : ""} · ${orientation}`);
    try {
      const r = await fetch(`${API}${M0}/${artifact.artifact_id}/cover`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ method, orientation, expected_version: artifact.version, ...extra }),
      });
      const d = await r.json();
      if (r.status === 409) { toast.error("Version conflict — reloaded"); await reload(); setBusy(""); setCoverBusy(false); return; }
      if (!r.ok) throw new Error(d.detail || "cover failed");
      setArtifact(d.artifact); loadPages(d.artifact); setInspection(null);
      pushLog("rex", `${d.note} (v${d.artifact.version}).`);
      toast.success("Cover updated");
    } catch (e) { toast.error(e.message); pushLog("rex", `Cover change failed: ${e.message}`); }
    setBusy(""); setCoverBusy(false);
  };

  const searchStock = async () => {
    if (!artifact) return;
    setStockLoading(true); setStockCands([]);
    try {
      const r = await fetch(`${API}${M0}/${artifact.artifact_id}/cover/search`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: coverQuery || undefined, orientation }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "search failed");
      setStockCands(d.candidates || []);
    } catch (e) { toast.error(e.message); }
    setStockLoading(false);
  };

  const pickStock = async (c) => {
    setShowStock(false);
    await setCover("upload", { image_url: c.full_url, attribution: { photographer: c.photographer, source_url: c.source_url } });
  };

  const saveOneDrive = async () => {
    const r = await fetch(`${API}${M0}/${artifact.artifact_id}/save-onedrive`, { method: "POST", credentials: "include" });
    const d = await r.json().catch(() => ({}));
    pushLog("rex", `OneDrive save is not part of M0: ${d.detail || "not implemented"}`);
    toast("OneDrive saving is out of scope for M0 (not simulated)");
  };

  return (
    <div className="min-h-screen bg-[#0d3b3c] text-white" data-testid="m0-proposal-studio">
      <div className="max-w-[1500px] mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[#c9a24b] tracking-[0.28em] text-xs uppercase">Milestone 0 · Quality &amp; editing proof</p>
            <h1 className="text-2xl font-semibold mt-1">Proposal Studio <span className="text-white/50 text-base font-normal">— Claude-led, Gamma-free</span></h1>
          </div>
          <div className="flex gap-2">
            <button data-testid="m0-create-btn" onClick={create} disabled={!!busy}
              className="px-4 py-2 rounded-full bg-[#c9a24b] text-[#0d3b3c] font-semibold text-sm hover:brightness-110 disabled:opacity-50">
              {artifact ? "Start a new draft" : "Create Union Bank proposal"}
            </button>
          </div>
        </div>

        {busy && <div data-testid="m0-busy" className="mb-4 text-sm text-[#c9a24b] animate-pulse">{busy}</div>}

        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs bg-white/5 border border-white/10 rounded-xl px-3 py-2" data-testid="m0-reference-bar">
          <span className="text-white/70 font-semibold">Reference library</span>
          <span className="text-white/40" data-testid="m0-reference-counts">{library.uploads.length} uploaded · {library.onedrive_count} from OneDrive</span>
          {library.uploads.slice(0, 4).map((u, i) => (
            <span key={i} className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 truncate max-w-[160px]">{u.file_name}</span>
          ))}
          <input ref={fileRef} type="file" accept=".pdf,.docx,.pptx,.txt,.md,.csv" onChange={uploadRef} className="hidden" data-testid="m0-reference-file" />
          <button data-testid="m0-reference-upload" disabled={uploading} onClick={() => fileRef.current?.click()}
            className="ml-auto px-3 py-1 rounded-full bg-[#c9a24b] text-[#0d3b3c] font-semibold disabled:opacity-50">
            {uploading ? "Reading…" : "Upload a brochure / past proposal"}
          </button>
          <span className="text-white/35 w-full mt-1">Rex studies these (brochures, past proposals, work samples) to match your style — plus your connected OneDrive folder.</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
          {/* LEFT: conversation */}
          <div className="flex flex-col bg-white/5 rounded-xl border border-white/10 overflow-hidden" style={{ height: "76vh" }}>
            <div ref={logRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="m0-conversation">
              {log.length === 0 && <p className="text-white/50 text-sm">Create the proposal, then revise it conversationally — "make the title bold", "change to six sessions", "update the fee".</p>}
              {log.map((m, i) => (
                <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`} data-testid={`m0-msg-${m.role}`}>
                  <span className={`inline-block px-3 py-2 rounded-2xl ${m.role === "user" ? "bg-[#c9a24b] text-[#0d3b3c]" : "bg-white/10 text-white/90"}`}>{m.text}</span>
                </div>
              ))}
            </div>
            {artifact && (
              <div className="p-3 border-t border-white/10">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} data-testid={`m0-suggest-${i}`} onClick={() => revise(s)} disabled={!!busy}
                      className="text-[11px] px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40">{s.length > 42 ? s.slice(0, 40) + "…" : s}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input data-testid="m0-revise-input" value={instruction} onChange={(e) => setInstruction(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && revise()} placeholder="Tell Rex what to change…"
                    className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm outline-none placeholder-white/40" />
                  <button data-testid="m0-revise-send" onClick={() => revise()} disabled={!!busy}
                    className="px-3 py-2 rounded-lg bg-[#c9a24b] text-[#0d3b3c] font-semibold text-sm disabled:opacity-50">Send</button>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: preview */}
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden flex flex-col" style={{ height: "76vh" }}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 text-sm">
              <div className="flex items-center gap-3">
                <span className="font-semibold">Preview</span>
                {artifact && <span data-testid="m0-version" className="text-white/50">v{artifact.version} · {pages.length} pages · total {artifact.commercials?.currency}{(artifact.commercials?.total || 0).toLocaleString()}</span>}
              </div>
              {artifact && (
                <div className="flex gap-2">
                  <a data-testid="m0-pdf-link" href={`${API}${M0}/${artifact.artifact_id}/pdf?v=${artifact.version}`} target="_blank" rel="noreferrer" className="text-[#c9a24b] hover:underline">Download PDF</a>
                  <button data-testid="m0-inspect-btn" onClick={inspect} disabled={!!busy} className="text-white/80 hover:text-white">Inspect</button>
                  <button data-testid="m0-save-onedrive-btn" onClick={saveOneDrive} className="text-white/50 hover:text-white/80">Save to OneDrive</button>
                </div>
              )}
            </div>
            {inspection && (
              <div data-testid="m0-inspection" className={`px-4 py-2 text-xs border-b border-white/10 ${inspection.verdict === "ready" ? "text-emerald-300" : "text-amber-300"}`}>
                Visual verdict: <b>{inspection.verdict}</b>{(inspection.issues || []).length ? ` — ${inspection.issues.map(i => i.what).join("; ")}` : " — no material issues"}
              </div>
            )}
            {artifact && (
              <div className="px-4 py-2.5 border-b border-white/10 bg-white/5 flex flex-wrap items-center gap-2 text-xs" data-testid="m0-cover-bar">
                <span className="text-white/70 font-semibold">Cover</span>
                <span className="text-white/40 mr-1">{artifact.cover?.method || "generate"} · {artifact.cover?.orientation || "portrait"}</span>
                <div className="flex rounded-full bg-white/10 p-0.5">
                  {["portrait", "landscape"].map((o) => (
                    <button key={o} data-testid={`m0-orient-${o}`} onClick={() => setOrientation(o)}
                      className={`px-2.5 py-1 rounded-full capitalize ${orientation === o ? "bg-[#c9a24b] text-[#0d3b3c] font-semibold" : "text-white/70"}`}>{o}</button>
                  ))}
                </div>
                <button data-testid="m0-cover-find-stock" disabled={coverBusy} onClick={() => { setShowStock(true); setStockCands([]); }}
                  className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40">Find stock photo</button>
                <button data-testid="m0-cover-brand" disabled={coverBusy} onClick={() => setCover("brand")}
                  className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40">Brand (no photo)</button>
                <input data-testid="m0-cover-url" value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)}
                  placeholder="image URL…" className="bg-white/10 rounded-full px-2.5 py-1 outline-none placeholder-white/30 w-24" />
                <button data-testid="m0-cover-upload" disabled={coverBusy || !coverUrl} onClick={() => setCover("upload", { image_url: coverUrl })}
                  className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40">Use my image</button>
                <select data-testid="m0-cover-focal" onChange={(e) => e.target.value && setCover("refit", { focal: e.target.value })}
                  className="bg-white/10 rounded-full px-2 py-1 text-white/70" defaultValue="">
                  <option value="" disabled>focal…</option>
                  {["center", "top", "bottom", "left", "right"].map((f) => <option key={f} value={f} className="text-black">{f}</option>)}
                </select>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-5 bg-[#e9ecef]" data-testid="m0-preview">
              {pages.length === 0 && <div className="text-[#0d3b3c]/50 text-sm text-center mt-20">No proposal yet. Click "Create Union Bank proposal".</div>}
              <div className="space-y-5 flex flex-col items-center">
                {pages.map((src, i) => (
                  <img key={i} src={src} alt={`page ${i + 1}`} data-testid={`m0-page-${i + 1}`}
                    className="w-full max-w-[720px] shadow-xl rounded-sm" />
                ))}
              </div>
            </div>
          </div>
        </div>

        <p className="text-white/35 text-[11px] mt-4" data-testid="m0-scaffold-note">
          M0 scaffold: context is a fixed approved Union Bank source set (no live CRM/OneDrive retrieval); durable execution and OneDrive saving are not implemented and are not simulated. The live proposal workflow is untouched.
        </p>
      </div>

      {showStock && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" data-testid="m0-stock-modal" onClick={() => setShowStock(false)}>
          <div className="bg-[#0d3b3c] border border-white/15 rounded-2xl w-full max-w-4xl max-h-[86vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-white/10 flex items-center gap-2">
              <span className="font-semibold text-sm">Find a cover photo</span>
              <span className="text-white/40 text-xs">Unsplash · Pexels · vision-screened</span>
              <div className="ml-auto flex gap-2">
                <input data-testid="m0-stock-query" value={coverQuery} onChange={(e) => setCoverQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchStock()} placeholder="e.g. african business leaders"
                  className="bg-white/10 rounded-full px-3 py-1.5 text-sm outline-none placeholder-white/30 w-56" />
                <button data-testid="m0-stock-search" onClick={searchStock} disabled={stockLoading}
                  className="px-3 py-1.5 rounded-full bg-[#c9a24b] text-[#0d3b3c] font-semibold text-sm disabled:opacity-50">Search</button>
                <button onClick={() => setShowStock(false)} className="px-2 py-1.5 text-white/60 hover:text-white text-sm">Close</button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto">
              {stockLoading && <div className="text-[#c9a24b] text-sm animate-pulse py-10 text-center">Searching and screening photos…</div>}
              {!stockLoading && stockCands.length === 0 && <div className="text-white/40 text-sm py-10 text-center">Type a search and press Search. Each photo is screened for competitor logos and text.</div>}
              <div className="grid grid-cols-3 gap-3">
                {stockCands.map((c, i) => (
                  <button key={i} data-testid={`m0-stock-candidate-${i}`} onClick={() => pickStock(c)}
                    className="relative group rounded-lg overflow-hidden border border-white/10 hover:border-[#c9a24b] text-left">
                    <img src={c.thumb_url} alt="" className="w-full h-32 object-cover" />
                    {c.screen?.flagged && (
                      <div data-testid={`m0-stock-warning-${i}`} className="absolute top-1.5 left-1.5 right-1.5 bg-amber-500/90 text-[#0d3b3c] text-[10px] font-semibold px-1.5 py-1 rounded">
                        ⚠ {c.screen.reason || "may contain a logo/text"}{c.screen.brands?.length ? ` (${c.screen.brands.join(", ")})` : ""}
                      </div>
                    )}
                    <div className="px-2 py-1.5 text-[10px] text-white/50 flex items-center justify-between">
                      <span className="truncate">{c.photographer || "stock"}</span>
                      {c.screen?.flagged ? <span className="text-amber-400">review</span> : <span className="text-emerald-400">clean</span>}
                    </div>
                  </button>
                ))}
              </div>
              {stockCands.length > 0 && <p className="text-white/30 text-[10px] mt-3">A warning means our vision check saw a possible competitor logo or text — you can still pick it (it will be used), or choose a clean one.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
